import { AlertTriangle, Calendar, Database, RefreshCw } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { Button } from "./Button";
import type { BackupStatus } from "./hooks/useBackupStatus";
import { useBackupStatus } from "./hooks/useBackupStatus";
import { Modal } from "./Modal";
import { Spinner } from "./Spinner";
import "./BackupsModal.css";

interface BackupsModalProps {
	isOpen: boolean;
	onClose: () => void;
}

interface BackupItem {
	id: string;
	name: string;
}

const formatBackupName = (name: string): string => {
	const match = name.match(
		/Backup_(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})/,
	);
	if (match) {
		const [_, date, hr, min, sec] = match;
		try {
			const d = new Date(`${date}T${hr}:${min}:${sec}Z`);
			if (!Number.isNaN(d.getTime())) {
				return d.toLocaleString(undefined, {
					dateStyle: "medium",
					timeStyle: "medium",
				});
			}
		} catch (_) {}
	}
	return name.replace("Backup_", "");
};

interface RestoreConfirmViewProps {
	selectedBackup: BackupItem;
	isPending: boolean;
	isRestoring: boolean;
	confirmText: string;
	setConfirmText: (text: string) => void;
	onCancel: () => void;
	onConfirm: () => void;
}

const RestoreConfirmView: React.FC<RestoreConfirmViewProps> = ({
	selectedBackup,
	isPending,
	isRestoring,
	confirmText,
	setConfirmText,
	onCancel,
	onConfirm,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	return (
		<div className="restore-confirm-container">
			<div className="alert-box danger">
				<AlertTriangle size={24} className="alert-icon" />
				<div className="alert-content">
					<h3 className="alert-title">Destructive Action Warning</h3>
					<p className="alert-desc">
						You are about to restore the database to the backup version:
					</p>
					<div className="backup-highlight-box">
						<Calendar size={16} className="calendar-icon" />
						<span className="backup-highlight-name">
							{formatBackupName(selectedBackup.name)}
						</span>
					</div>
					<p className="alert-warning-text">
						This will <strong>completely overwrite</strong> all current notes,
						resumes, and interaction records. This action cannot be undone.
					</p>
				</div>
			</div>

			<div className="confirm-input-group">
				<label htmlFor="restore-confirm-input" className="confirm-label">
					To confirm, type <span className="highlight-text">RESTORE</span>{" "}
					below:
				</label>
				<input
					ref={inputRef}
					id="restore-confirm-input"
					type="text"
					className="confirm-input"
					placeholder="Type RESTORE"
					value={confirmText}
					onChange={(e) => setConfirmText(e.target.value)}
					disabled={isPending}
				/>
			</div>

			<div className="confirm-actions">
				<Button variant="secondary" onClick={onCancel} disabled={isPending}>
					Cancel
				</Button>
				<Button
					variant="primary"
					className="btn-danger"
					onClick={onConfirm}
					disabled={confirmText !== "RESTORE" || isPending}
				>
					{isRestoring ? (
						<div className="btn-spinner-content">
							<Spinner size="small" />
							<span>Restoring...</span>
						</div>
					) : (
						"Confirm Restore"
					)}
				</Button>
			</div>
		</div>
	);
};

const getStatusText = (
	isPending: boolean,
	isBackingUp: boolean,
	operationStatus: BackupStatus | null,
): string => {
	if (isBackingUp) return "Backup in progress...";
	if (isPending) return "Restoring database...";
	if (operationStatus?.state === "success") {
		return `Last ${operationStatus.operation || "operation"} succeeded`;
	}
	if (operationStatus?.state === "error") {
		return `Last ${operationStatus.operation || "operation"} failed: ${operationStatus.message}`;
	}
	return "System ready";
};

interface BackupsListContentProps {
	isLoadingBackups: boolean;
	backups: BackupItem[];
	isPending: boolean;
	onRestoreClick: (backup: BackupItem) => void;
}

const BackupsListContent: React.FC<BackupsListContentProps> = ({
	isLoadingBackups,
	backups,
	isPending,
	onRestoreClick,
}) => {
	if (isLoadingBackups && backups.length === 0) {
		return (
			<div className="list-loading-state">
				<Spinner size="medium" />
				<p>Loading backups from Google Drive...</p>
			</div>
		);
	}

	if (backups.length === 0) {
		return (
			<div className="list-empty-state">
				<Database size={32} className="empty-icon" />
				<p>No backups found on Google Drive</p>
			</div>
		);
	}

	return (
		<div className="backups-list">
			{backups.map((item) => (
				<div key={item.id} className="backup-item">
					<div className="backup-info">
						<span className="backup-date">{formatBackupName(item.name)}</span>
						<span className="backup-folder-id">{item.id}</span>
					</div>
					<Button
						variant="secondary"
						onClick={() => onRestoreClick(item)}
						disabled={isPending}
						className="restore-trigger-btn"
					>
						Restore
					</Button>
				</div>
			))}
		</div>
	);
};

interface BackupsListViewProps {
	isPending: boolean;
	isBackingUp: boolean;
	operationStatus: BackupStatus | null;
	backups: BackupItem[];
	isLoadingBackups: boolean;
	onTriggerBackup: () => void;
	onFetchBackups: () => void;
	onRestoreClick: (backup: BackupItem) => void;
}

const BackupsListView: React.FC<BackupsListViewProps> = ({
	isPending,
	isBackingUp,
	operationStatus,
	backups,
	isLoadingBackups,
	onTriggerBackup,
	onFetchBackups,
	onRestoreClick,
}) => {
	const statusText = getStatusText(isPending, isBackingUp, operationStatus);
	const statusClass = isPending ? "loading" : operationStatus?.state || "idle";

	return (
		<div className="backups-modal-content">
			<div className="status-banner-container">
				<div className={`status-badge ${statusClass}`}>
					<span className="status-dot" />
					<span className="status-text">{statusText}</span>
				</div>

				<Button
					variant="primary"
					onClick={onTriggerBackup}
					disabled={isPending}
					className="backup-now-btn"
				>
					{isBackingUp ? (
						<div className="btn-spinner-content">
							<Spinner size="small" />
							<span>Backing Up...</span>
						</div>
					) : (
						<div className="btn-icon-content">
							<Database size={16} />
							<span>Backup Now</span>
						</div>
					)}
				</Button>
			</div>

			<div className="backups-list-section">
				<div className="section-header">
					<h3 className="section-title">Available Backups</h3>
					<button
						type="button"
						className="refresh-btn"
						onClick={onFetchBackups}
						disabled={isLoadingBackups || isPending}
						title="Refresh list"
						aria-label="Refresh list"
					>
						<RefreshCw
							size={14}
							className={isLoadingBackups ? "animate-spin" : ""}
						/>
					</button>
				</div>

				<BackupsListContent
					isLoadingBackups={isLoadingBackups}
					backups={backups}
					isPending={isPending}
					onRestoreClick={onRestoreClick}
				/>
			</div>
		</div>
	);
};

export const BackupsModal: React.FC<BackupsModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { isAuthenticated } = useAuth();
	const {
		isPending,
		isBackingUp,
		isRestoring,
		operationStatus,
		backups,
		isLoadingBackups,
		fetchBackups,
		triggerBackup,
		triggerRestore,
	} = useBackupStatus(isAuthenticated);

	const [selectedBackup, setSelectedBackup] = useState<BackupItem | null>(null);
	const [confirmText, setConfirmText] = useState("");

	useEffect(() => {
		if (isOpen && isAuthenticated) {
			fetchBackups();
		}
	}, [isOpen, isAuthenticated, fetchBackups]);

	const handleRestoreClick = (backup: BackupItem) => {
		setSelectedBackup(backup);
		setConfirmText("");
	};

	const handleCancelRestore = () => {
		setSelectedBackup(null);
		setConfirmText("");
	};

	const handleConfirmRestore = async () => {
		if (!selectedBackup || confirmText !== "RESTORE") return;
		await triggerRestore(selectedBackup.id);
		setSelectedBackup(null);
		setConfirmText("");
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={isPending ? undefined : onClose}
			title={selectedBackup ? "Confirm Restore" : "Backup & Restore Management"}
		>
			{selectedBackup ? (
				<RestoreConfirmView
					selectedBackup={selectedBackup}
					isPending={isPending}
					isRestoring={isRestoring}
					confirmText={confirmText}
					setConfirmText={setConfirmText}
					onCancel={handleCancelRestore}
					onConfirm={handleConfirmRestore}
				/>
			) : (
				<BackupsListView
					isPending={isPending}
					isBackingUp={isBackingUp}
					operationStatus={operationStatus}
					backups={backups}
					isLoadingBackups={isLoadingBackups}
					onTriggerBackup={triggerBackup}
					onFetchBackups={fetchBackups}
					onRestoreClick={handleRestoreClick}
				/>
			)}
		</Modal>
	);
};
